/* ============================================
   MEDILANE-LOGO — wiederverwendbar
   ============================================
   - Mark: nur das runde Tile (m-Symbol mit Punkt)
   - Lockup: Mark + Wortmarke "Medilane" + Claim

   Auf hellen Flächen wird das echte PNG-Logo
   gezeigt, auf dunklen Flächen eine inverse
   SVG-Variante (Mark + weißer Wortmark).
   ============================================ */

import './Logo.css'

interface MarkProps {
  size?: number
  /** "ink" = echte Marke auf hellem Grund · "inverse" = weißer Mark auf dunklem Grund. */
  variant?: 'ink' | 'inverse'
  title?: string
}

export function MedilaneMark({ size = 36, variant = 'ink', title = 'Medilane' }: MarkProps) {
  if (variant === 'inverse') {
    // SVG-Rekonstruktion mit weißem Tile + dunklem m für dunkle Hintergründe
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        aria-label={title}
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="50" cy="50" r="48" fill="#FFFFFF" />
        <g transform="translate(28 35)">
          <path d="M3 33 L 3 11 A 11 11 0 0 1 25 11 L 25 33" fill="none" stroke="#0F172A" strokeWidth={5} strokeLinecap="round" />
          <path d="M25 33 L 25 11 A 11 11 0 0 1 47 11 L 47 33" fill="none" stroke="#0F172A" strokeWidth={5} strokeLinecap="round" />
        </g>
        <circle cx="76" cy="68" r="3.5" fill="#0D9488" />
      </svg>
    )
  }

  // Original-PNG aus dem Markenleitfaden
  return (
    <img
      src="/medilane-mark-256.png"
      srcSet="/medilane-mark-256.png 1x, /medilane-mark-512.png 2x"
      width={size}
      height={size}
      alt={title}
      className="ml-mark-img"
      style={{ width: size, height: size }}
    />
  )
}

interface LockupProps {
  /** Höhe des Marks (in px). Wortmarke skaliert proportional. */
  size?: number
  variant?: 'ink' | 'inverse'
  /** Claim „PFLEGE MIT ZUKUNFT" unter dem Wortmark anzeigen. */
  withClaim?: boolean
  className?: string
}

export function MedilaneLockup({
  size = 36,
  variant = 'ink',
  withClaim = false,
  className,
}: LockupProps) {
  // Inverse-Variante: weißer SVG-Mark + weißer Wortmark als Text
  if (variant === 'inverse') {
    return (
      <div className={`ml-lockup ${className || ''}`}>
        <MedilaneMark size={size} variant="inverse" />
        <div className="ml-lockup-divider" style={{ background: 'rgba(255,255,255,0.22)', height: size * 0.7 }} />
        <div className="ml-lockup-text">
          <span
            className="ml-lockup-word"
            style={{ color: '#FFFFFF', fontSize: size * 0.62, letterSpacing: '-0.03em', lineHeight: 1 }}
          >
            Medilane
          </span>
          {withClaim && (
            <span
              className="ml-lockup-claim"
              style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: Math.max(8, size * 0.18),
                letterSpacing: '0.22em',
                marginTop: size * 0.12,
              }}
            >
              PFLEGE MIT ZUKUNFT
            </span>
          )}
        </div>
      </div>
    )
  }

  // Standard: das originale Lockup-PNG. Wir nutzen das Full-Lockup-Bild,
  // weil es Mark + Wortmark + Claim als ein in sich stimmiges Asset enthält.
  // Höhe steuert die Skalierung, Breite ergibt sich aus dem Bildverhältnis.
  // Falls "withClaim" false: simpleren Lockup ohne Claim zeigen (Mark + Wort).
  if (withClaim) {
    return (
      <img
        src="/medilane-logo.png"
        alt="Medilane — Pflege mit Zukunft"
        className={`ml-lockup-img ${className || ''}`}
        style={{ height: size * 1.4, width: 'auto' }}
      />
    )
  }

  return (
    <div className={`ml-lockup ${className || ''}`}>
      <MedilaneMark size={size} variant="ink" />
      <div className="ml-lockup-divider" style={{ background: 'rgba(15,23,42,0.22)', height: size * 0.7 }} />
      <span
        className="ml-lockup-word"
        style={{ color: '#0F172A', fontSize: size * 0.62, letterSpacing: '-0.03em', lineHeight: 1 }}
      >
        Medilane
      </span>
    </div>
  )
}
